import React from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
} from '@mui/material';
import {
  ITableFilterType,
  TableFilterType,
} from './index';

interface EnhancedTableFiltersProps {
  filters: ITableFilterType[];
  filterValues: Record<string, any>;
  onFilterChange: (name: string, value: any) => void;
  onResetFilters: () => void;
  data: any[];
  horizontal?: boolean;
}

const EnhancedTableFilters: React.FC<EnhancedTableFiltersProps> = ({
  filters,
  filterValues,
  onFilterChange,
  onResetFilters,
  horizontal = false,
}) => {
  if (horizontal) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap', p: 0, pl: 0, width: '100%', mb: 1 }}>
        {filters.map((filter) => {
          const value = filterValues[filter.id] ?? '';
          switch (filter.type) {
            case TableFilterType.SELECT:
            case TableFilterType.MULTISELECT:
              const options =
                typeof filter.options === 'function'
                  ? filter.options([])
                  : filter.options;
              return (
                <Box key={filter.id} sx={{ minWidth: { xs: 140, sm: 180 }, flex: '1 1 180px', mb: 1 }}>
                  <TextField
                    select
                    fullWidth
                    label={filter.label}
                    value={value}
                    onChange={(e) => onFilterChange(filter.id, e.target.value)}
                    variant="outlined"
                    size="small"
                  >
                    <MenuItem value="">All</MenuItem>
                    {options.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              );

            case TableFilterType.TEXT:
              return (
                <Box key={filter.id} sx={{ minWidth: { xs: 140, sm: 180 }, flex: '1 1 180px', mb: 1 }}>
                  <TextField
                    fullWidth
                    label={filter.label}
                    placeholder={filter.placeholder}
                    value={value}
                    onChange={(e) => onFilterChange(filter.id, e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              );

            case TableFilterType.NUMBER:
              return (
                <Box key={filter.id} sx={{ minWidth: { xs: 140, sm: 180 }, flex: '1 1 180px', mb: 1 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label={filter.label}
                    value={value}
                    onChange={(e) => onFilterChange(filter.id, e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              );

            case TableFilterType.DATE:
              return (
                <Box key={filter.id} sx={{ minWidth: { xs: 140, sm: 180 }, flex: '1 1 180px', mb: 1 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label={filter.label}
                    InputLabelProps={{ shrink: true }}
                    value={value}
                    onChange={(e) => onFilterChange(filter.id, e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              );

            case TableFilterType.BOOLEAN:
              return (
                <Box key={filter.id} sx={{ minWidth: { xs: 140, sm: 180 }, flex: '1 1 180px', mb: 1 }}>
                  <TextField
                    select
                    fullWidth
                    label={filter.label}
                    value={value}
                    onChange={(e) => onFilterChange(filter.id, e.target.value === 'true')}
                    variant="outlined"
                    size="small"
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="true">True</MenuItem>
                    <MenuItem value="false">False</MenuItem>
                  </TextField>
                </Box>
              );

            default:
              return null;
          }
        })}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
          <Button onClick={onResetFilters} size="small" sx={{ ml: 2, mb: 0.5 }}>
            Clear Filters
          </Button>
        </Box>
      </Box>
    );
  }
  // Default vertical layout
  return (
    <Box sx={{ p: 1, pl: 0 }}>
      <Grid container spacing={1} alignItems="flex-end">
        {filters.map((filter) => {
          const value = filterValues[filter.id] ?? '';
          switch (filter.type) {
            case TableFilterType.SELECT:
            case TableFilterType.MULTISELECT:
              const options =
                typeof filter.options === 'function'
                  ? filter.options([])
                  : filter.options;
              return (
                <Grid item xs={12} sm={6} md={3} key={filter.id}>
                  <TextField
                    select
                    fullWidth
                    label={filter.label}
                    value={value}
                    onChange={(e) => onFilterChange(filter.id, e.target.value)}
                    variant="outlined"
                    size="small"
                  >
                    <MenuItem value="">All</MenuItem>
                    {options.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              );

            case TableFilterType.TEXT:
              return (
                <Grid item xs={12} sm={6} md={3} key={filter.id}>
                  <TextField
                    fullWidth
                    label={filter.label}
                    placeholder={filter.placeholder}
                    value={value}
                    onChange={(e) => onFilterChange(filter.id, e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              );

            case TableFilterType.NUMBER:
              return (
                <Grid item xs={12} sm={6} md={3} key={filter.id}>
                  <TextField
                    fullWidth
                    type="number"
                    label={filter.label}
                    value={value}
                    onChange={(e) => onFilterChange(filter.id, e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              );

            case TableFilterType.DATE:
              return (
                <Grid item xs={12} sm={6} md={3} key={filter.id}>
                  <TextField
                    fullWidth
                    type="date"
                    label={filter.label}
                    InputLabelProps={{ shrink: true }}
                    value={value}
                    onChange={(e) => onFilterChange(filter.id, e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              );

            case TableFilterType.BOOLEAN:
              return (
                <Grid item xs={12} sm={6} md={3} key={filter.id}>
                  <TextField
                    select
                    fullWidth
                    label={filter.label}
                    value={value}
                    onChange={(e) => onFilterChange(filter.id, e.target.value === 'true')}
                    variant="outlined"
                    size="small"
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="true">True</MenuItem>
                    <MenuItem value="false">False</MenuItem>
                  </TextField>
                </Grid>
              );

            default:
              return null;
          }
        })}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={onResetFilters} size="small">
              Clear Filters
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EnhancedTableFilters;
